import React, { useState, useEffect } from "react";
import { imageUrl, postCommitment, LEGACY_PDF } from "@/lib/api";
import { galleryKeyFor } from "@/portal/portal";

// ---- localStorage-backed worksheet answer (keyed by question id) ----
function AnswerField({ qid, label, t }) {
  const key = `rbl_ans_${qid}`;
  const [val, setVal] = useState(() => localStorage.getItem(key) || "");
  useEffect(() => {
    const h = setTimeout(() => localStorage.setItem(key, val), 300);
    return () => clearTimeout(h);
  }, [val, key]);
  return (
    <div className="rbl-field">
      <label className="rbl-label">{label}</label>
      <textarea className="rbl-input" data-testid={`ws-${qid}`} placeholder={t("typeHere")}
        value={val} onChange={(e) => setVal(e.target.value)} />
    </div>
  );
}

function Gallery({ ids, title, t }) {
  return (
    <>
      {title ? <h2 className="rbl-block-h">{title}</h2> : null}
      {ids && ids.length ? (
        <div className="rbl-gallery" data-testid="session-gallery">
          {ids.map((id) => <img key={id} src={imageUrl(id)} alt="" loading="lazy" />)}
        </div>
      ) : (
        <p style={{ color: "#8a90a2" }}>{t("noImages")}</p>
      )}
    </>
  );
}

function Quiz({ sec, t }) {
  const [picked, setPicked] = useState({});
  return (
    <div className="rbl-quiz" data-testid="session-quiz">
      <h4>{sec.title}</h4>
      <p className="hint">{t("quizHint")}</p>
      {sec.questions.map((q, qi) => {
        const chosen = picked[q.id];
        return (
          <div className="rbl-q" key={q.id}>
            <div className="rbl-q-text">{qi + 1}. {q.prompt}</div>
            {q.options.map((opt, oi) => {
              let cls = "rbl-opt";
              if (chosen != null) {
                if (oi === q.correct) cls += " correct";
                else if (oi === chosen) cls += " wrong";
              }
              return (
                <button key={oi} className={cls} data-testid={`quiz-${q.id}-opt${oi}`}
                  onClick={() => setPicked((p) => ({ ...p, [q.id]: oi }))}>
                  {String.fromCharCode(65 + oi)}. {opt}
                </button>
              );
            })}
            {chosen != null && (
              <div className={`rbl-feedback ${chosen === q.correct ? "ok" : "no"}`}>
                {chosen === q.correct ? t("quizCorrect") : t("quizWrong")}
                {q.explain ? ` ${q.explain}` : ""}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Worksheet({ sec, t }) {
  const baseQs = sec.questions || [];
  const storeKey = `rbl_ws_copies_${baseQs[0]?.id || sec.title}`;
  const [copies, setCopies] = useState(() => Number(localStorage.getItem(storeKey) || 0));
  const addCopy = () => {
    const n = copies + 1;
    setCopies(n);
    localStorage.setItem(storeKey, String(n));
  };
  return (
    <div className="rbl-worksheet" data-testid="session-worksheet">
      <h4>{sec.title}</h4>
      <div className="hint">{t("worksheetHint")}</div>
      {baseQs.map((q) => <AnswerField key={q.id} qid={q.id} label={q.label} t={t} />)}
      {sec.multi && Array.from({ length: copies }).map((_, ci) => (
        <div key={ci} style={{ borderTop: "1px dashed var(--line)", marginTop: 18, paddingTop: 10 }}>
          <div className="rbl-label" style={{ color: "var(--muted)" }}>{t("wsCopy")} {ci + 2}</div>
          {baseQs.map((q) => <AnswerField key={q.id} qid={`${q.id}_copy${ci + 1}`} label={q.label} t={t} />)}
        </div>
      ))}
      {sec.multi && (
        <button className="rbl-link" data-testid="ws-add-copy" onClick={addCopy}
          style={{ border: "none", background: "none", marginTop: 10 }}>{t("wsAddCopy")}</button>
      )}
    </div>
  );
}

function ExpectationConversations({ t }) {
  const key = "rbl_ec_conversations";
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(key) || "[]"); } catch { return []; }
  });
  const fields = [
    ["person", t("ecPerson"), t("ecPersonPh")],
    ["barriers", t("ecBarriers"), ""],
    ["motive", t("ecMotive"), ""],
    ["topics", t("ecTopics"), ""],
    ["questions", t("ecQuestions"), t("ecQuestionsPh")],
    ["criteria", t("ecCriteria"), ""],
  ];
  const save = (next) => { setItems(next); localStorage.setItem(key, JSON.stringify(next)); };
  const add = () => save([...items, {}]);
  const update = (i, f, v) => { const n = [...items]; n[i] = { ...n[i], [f]: v }; save(n); };
  const remove = (i) => save(items.filter((_, x) => x !== i));
  return (
    <div className="rbl-worksheet" data-testid="expectation-conversations">
      <p style={{ color: "#46506a" }}>{t("ecIntro")}</p>
      {items.length === 0 && <p style={{ color: "#8a90a2" }}>{t("ecEmpty")}</p>}
      {items.map((it, i) => (
        <div key={i} style={{ borderTop: "1px solid var(--line)", paddingTop: 12, marginTop: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <strong>{t("ecConversation")} {i + 1}</strong>
            <button className="rbl-link" onClick={() => remove(i)} style={{ border: "none", background: "none" }}>{t("ecAdd").includes("Add") ? "Remove" : t("wsRemove")}</button>
          </div>
          {fields.map(([f, label, ph]) => (
            <div className="rbl-field" key={f}>
              <label className="rbl-label">{label}</label>
              <textarea className="rbl-input" placeholder={ph} value={it[f] || ""}
                onChange={(e) => update(i, f, e.target.value)} />
            </div>
          ))}
        </div>
      ))}
      <button className="rbl-btn" data-testid="ec-add" onClick={add} style={{ marginTop: 12 }}>＋ {t("ecAdd")}</button>
    </div>
  );
}

function CommitmentForm({ t }) {
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (text.trim().length < 5) return;
    setBusy(true);
    try {
      await postCommitment({ name, commitment: text });
      localStorage.setItem("rbl_ans_monday_name", name);
      localStorage.setItem("rbl_ans_monday_commitment", text);
      setDone(true);
    } finally { setBusy(false); }
  };
  if (done) return (
    <div className="rbl-ok" data-testid="commitment-success">
      <strong>{t("mmDoneTitle")}</strong><div>{t("mmDoneBody")}</div>
    </div>
  );
  return (
    <div className="rbl-worksheet" data-testid="commitment-form">
      <div className="rbl-field">
        <label className="rbl-label">{t("mmNameLabel")}</label>
        <input className="rbl-input" data-testid="commitment-name" placeholder={t("mmNamePh")}
          value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="rbl-field">
        <label className="rbl-label">{t("mmCommitLabel")}</label>
        <textarea className="rbl-input" data-testid="commitment-text" placeholder={t("mmCommitPh")}
          value={text} onChange={(e) => setText(e.target.value)} />
      </div>
      <button className="rbl-btn green block" data-testid="commitment-submit" disabled={busy || text.trim().length < 5} onClick={submit}>
        {t("mmSubmit")}
      </button>
    </div>
  );
}

export default function SessionBlocks({ sections, sessionMeta, metaBySession, t, renderExtra }) {
  return (
    <div className="rbl-prose" data-testid="session-content">
      {sections.map((sec, i) => {
        const kind = sec.kind;
        const extra = renderExtra && renderExtra(sec, i);
        if (extra) return extra;
        if (kind === "theory") {
          return (
            <div key={i}>
              {sec.title && <h2 className="rbl-block-h">{sec.title}</h2>}
              {sec.bullets && <ul>{sec.bullets.map((b, j) => <li key={j}>{b}</li>)}</ul>}
            </div>
          );
        }
        if (kind === "callout") {
          return (
            <div className="rbl-note" key={i} data-testid="session-callout">
              {sec.title && <strong style={{ display: "block", marginBottom: 4 }}>{sec.title}</strong>}
              {sec.body}
            </div>
          );
        }
        if (kind === "table") {
          return (
            <div key={i}>
              {sec.title && <h2 className="rbl-block-h">{sec.title}</h2>}
              <div className="rbl-table-wrap">
                <table className="rbl-table">
                  {sec.headers && <thead><tr>{sec.headers.map((h, j) => <th key={j}>{h}</th>)}</tr></thead>}
                  <tbody>{sec.rows.map((r, j) => <tr key={j}>{r.map((c, k) => <td key={k}>{c}</td>)}</tr>)}</tbody>
                </table>
              </div>
            </div>
          );
        }
        if (kind === "quiz") return <Quiz key={i} sec={sec} t={t} />;
        if (kind === "worksheet") return <Worksheet key={i} sec={sec} t={t} />;
        if (kind === "expectation-conversations") {
          return (
            <div key={i}>
              <h2 className="rbl-block-h">{sec.title}</h2>
              <ExpectationConversations t={t} />
            </div>
          );
        }
        if (kind === "case-grid") {
          return (
            <div key={i}>
              <h2 className="rbl-block-h">{sec.title}</h2>
              <div className="rbl-cases">
                {sec.cases.map((c, j) => (
                  <div className="rbl-case" key={j}>
                    <span className="rbl-case-n">{String(c.number).padStart(2, "0")}</span>
                    <h4>{c.title}</h4>
                    <div className="desc">{c.scenario}</div>
                    <ul>{c.questions.map((q, k) => <li key={k}>{q}</li>)}</ul>
                  </div>
                ))}
              </div>
            </div>
          );
        }
        if (kind === "feedback360") {
          return (
            <div className="rbl-feature" key={i} data-testid="feedback360" style={{ margin: "18px 0" }}>
              <h3>{sec.title}</h3>
              <p>{sec.subtitle}</p>
              <a className="rbl-btn" href={LEGACY_PDF.feedback360("en")} target="_blank" rel="noreferrer">{sec.cta}</a>
            </div>
          );
        }
        if (kind === "download") {
          const url = (sessionMeta && sec.sessionKey && sessionMeta[sec.sessionKey]) || "#";
          return (
            <div className="rbl-feature" key={i} data-testid="session-download" style={{ margin: "18px 0" }}>
              <h3>{sec.title}</h3>
              {sec.subtitle && <p>{sec.subtitle}</p>}
              <a className="rbl-btn" href={url} target="_blank" rel="noreferrer">{sec.cta}</a>
            </div>
          );
        }
        if (kind === "commitment" || (sec.kind && sec.kind.includes("commitment"))) {
          return <CommitmentForm key={i} t={t} />;
        }
        if (kind && kind.endsWith("-gallery")) {
          const galKey = galleryKeyFor(kind);
          const meta = (sec.session && metaBySession && metaBySession[sec.session]) || sessionMeta;
          const ids = meta ? (meta[galKey] || []) : [];
          return <Gallery key={i} ids={ids} title={sec.title} t={t} />;
        }
        return null;
      })}
    </div>
  );
}

export { CommitmentForm };
